import { api } from './axios'
import type { ApiResponse, AuthResponse, LoginResponse, User, AuthFacility } from '@/types'

export interface LoginInput {
  email: string
  password: string
}

export interface RegisterInput {
  email: string
  password: string
  firstName: string
  lastName: string
  phone?: string
  facility: {
    name: string
    address: string
    city: string
    postalCode?: string
    phone: string
    email?: string
    bedCount: number
    bulstat?: string
    registrationNumber?: string
  }
}

export interface AcceptInviteInput {
  token: string
  password: string
}

export interface ForgotPasswordInput {
  email: string
}

export interface ResetPasswordInput {
  token: string
  password: string
}

export interface VerifyEmailInput {
  token: string
}

export interface Verify2FAInput {
  challengeToken: string
  code: string
}

export const authService = {
  async getCurrentUser(): Promise<{ user: User; facility: AuthFacility | null }> {
    const response =
      await api.get<ApiResponse<{ user: User; facility: AuthFacility | null }>>('/user/me')
    return response.data.data
  },

  async login(data: LoginInput): Promise<LoginResponse> {
    const response = await api.post<ApiResponse<LoginResponse>>('/auth/login', data)
    return response.data.data
  },

  async verify2FA(data: Verify2FAInput): Promise<AuthResponse> {
    const response = await api.post<ApiResponse<AuthResponse>>('/auth/verify-2fa', data)
    return response.data.data
  },

  async setup2FA(): Promise<{ qrCodeDataUrl: string; secret: string }> {
    const response =
      await api.post<ApiResponse<{ qrCodeDataUrl: string; secret: string }>>('/auth/2fa/setup')
    return response.data.data
  },

  async confirm2FA(code: string): Promise<{ twoFactorEnabled: boolean }> {
    const response = await api.post<ApiResponse<{ twoFactorEnabled: boolean }>>(
      '/auth/2fa/confirm',
      { code }
    )
    return response.data.data
  },

  async disable2FA(code: string): Promise<{ twoFactorEnabled: boolean }> {
    const response = await api.post<ApiResponse<{ twoFactorEnabled: boolean }>>(
      '/auth/2fa/disable',
      { code }
    )
    return response.data.data
  },

  async register(data: RegisterInput): Promise<AuthResponse> {
    const response = await api.post<ApiResponse<AuthResponse>>('/auth/register', data)
    return response.data.data
  },

  async logout(): Promise<void> {
    await api.post('/auth/logout')
  },

  async acceptInvite(data: AcceptInviteInput): Promise<AuthResponse> {
    const response = await api.post<ApiResponse<AuthResponse>>('/auth/accept-invite', data)
    return response.data.data
  },

  async forgotPassword(data: ForgotPasswordInput): Promise<void> {
    await api.post('/auth/forgot-password', data)
  },

  async resetPassword(data: ResetPasswordInput): Promise<void> {
    await api.post('/auth/reset-password', data)
  },

  async verifyEmail(data: VerifyEmailInput): Promise<void> {
    await api.post('/auth/verify-email', data)
  },

  async resendVerificationEmail(): Promise<void> {
    await api.post('/auth/resend-verification')
  },
}
